import { useEffect, useState } from 'react';
import { Check, MessageSquare, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Popover } from '@/components/ui/Popover';
import { Tooltip } from '@/components/ui/Tooltip';
import { toast } from 'sonner';
import { cn } from '@/lib/cn';
import {
  useDeleteVendorSubcategoryDecision,
  useUpsertVendorSubcategoryDecision,
} from '@/api/bi/hooks';
import type { VendorDecision, VendorSubcategoryDecisionLite } from '@/api/bi/types';

/**
 * `VendorDecisionPicker` — three-state segmented control + note popover.
 *
 * Renders inline on every (subcategory, vendor) pivot row. Hits
 * `useUpsertVendorSubcategoryDecision` on click and shows a tiny pulse
 * to confirm. The note popover deliberately defers POST until the user
 * blurs/saves so a chatty typist doesn't trigger a flood of writes.
 */

interface Props {
  vendorId: number;
  subcategoryId: number;
  current?: VendorSubcategoryDecisionLite | null;
  /** Compact mode: reduces button widths to fit inside table rows. */
  compact?: boolean;
}

const OPTIONS: Array<{
  value: VendorDecision;
  label: string;
  hint: string;
  className: string;
}> = [
  {
    value: 'maximize',
    label: 'Max',
    hint: 'Push more business — feature, restock, increase share',
    className:
      'data-[on=true]:bg-success/15 data-[on=true]:text-success data-[on=true]:border-success/40',
  },
  {
    value: 'neutral',
    label: 'Hold',
    hint: 'Keep monitoring — no active push or wind-down',
    className:
      'data-[on=true]:bg-muted data-[on=true]:text-foreground data-[on=true]:border-border',
  },
  {
    value: 'minimize',
    label: 'Min',
    hint: 'Wind down — let stock run out, redirect to alt vendor',
    className:
      'data-[on=true]:bg-danger/15 data-[on=true]:text-danger data-[on=true]:border-danger/40',
  },
];

export function VendorDecisionPicker({
  vendorId,
  subcategoryId,
  current,
  compact,
}: Props) {
  const upsert = useUpsertVendorSubcategoryDecision();
  const remove = useDeleteVendorSubcategoryDecision();
  const [noteOpen, setNoteOpen] = useState(false);
  const [draftNote, setDraftNote] = useState(current?.note ?? '');

  // Keep the draft note in sync when the upstream row changes (e.g. after
  // a refetch).
  useEffect(() => {
    setDraftNote(current?.note ?? '');
  }, [current?.note, current?.id]);

  const set = (decision: VendorDecision) => {
    upsert.mutate(
      { vendor_id: vendorId, subcategory_id: subcategoryId, decision },
      {
        onError: () => toast.error('Could not save decision'),
      },
    );
  };

  const saveNote = () => {
    upsert.mutate(
      {
        vendor_id: vendorId,
        subcategory_id: subcategoryId,
        decision: current?.decision ?? 'neutral',
        note: draftNote,
      },
      {
        onSuccess: () => {
          setNoteOpen(false);
          toast.success('Note saved');
        },
        onError: () => toast.error('Could not save note'),
      },
    );
  };

  const clear = () => {
    if (!current?.id) return;
    remove.mutate(current.id, {
      onSuccess: () => {
        setNoteOpen(false);
        toast.success('Decision cleared');
      },
      onError: () => toast.error('Could not clear decision'),
    });
  };

  return (
    <div className="inline-flex items-center gap-1">
      <div className="inline-flex overflow-hidden rounded-md border border-border bg-surface">
        {OPTIONS.map((opt, i) => {
          const isActive = current?.decision === opt.value;
          return (
            <Tooltip key={opt.value} content={opt.hint}>
              <button
                type="button"
                data-on={isActive}
                disabled={upsert.isPending}
                onClick={() => set(opt.value)}
                className={cn(
                  'border-r border-border px-2 text-[11px] font-medium uppercase tracking-wide transition-colors',
                  i === OPTIONS.length - 1 && 'border-r-0',
                  compact ? 'h-6' : 'h-7',
                  'text-muted-foreground hover:bg-muted/50',
                  opt.className,
                )}
              >
                {opt.label}
              </button>
            </Tooltip>
          );
        })}
      </div>

      <Popover
        open={noteOpen}
        onOpenChange={setNoteOpen}
        side="left"
        align="start"
        trigger={
          <button
            type="button"
            className={cn(
              'inline-flex items-center justify-center rounded-md border border-border text-muted-foreground transition-colors hover:bg-muted/40',
              compact ? 'h-6 w-6' : 'h-7 w-7',
              current?.note && 'text-primary',
            )}
            title={current?.note ? 'Edit note' : 'Add note'}
          >
            <MessageSquare className="h-3 w-3" />
          </button>
        }
      >
        <div className="space-y-2">
          <p className="text-xs font-medium text-muted-foreground">Decision note</p>
          <Input
            value={draftNote}
            onChange={(e) => setDraftNote(e.target.value)}
            placeholder="Why this call? (optional)"
            maxLength={300}
          />
          <div className="flex items-center justify-between">
            {current?.id ? (
              <button
                type="button"
                onClick={clear}
                className="inline-flex items-center gap-1 text-xs text-destructive hover:underline"
              >
                <Trash2 className="h-3 w-3" />
                Clear decision
              </button>
            ) : (
              <span className="text-[10px] text-muted-foreground">
                Pick a status first
              </span>
            )}
            <Button
              type="button"
              size="sm"
              onClick={saveNote}
              disabled={upsert.isPending || !current}
            >
              <Check className="mr-1 h-3 w-3" />
              Save
            </Button>
          </div>
        </div>
      </Popover>
    </div>
  );
}
